<script setup lang="ts">
import { computed, watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { NConfigProvider, NDialogProvider, NGlobalStyle, NMessageProvider } from 'naive-ui'
import { koraNaiveThemeOverrides } from '../../../libs/design-tokens/kora'
import { NAIVE_LOCALES } from '@/theme/naiveLocale'
import { useEffectiveLocale } from '@/composables/useEffectiveLocale'
import { useLocaleSync } from '@/composables/useLocaleSync'
import { useAuthStore } from '@/stores/auth'

const themeOverrides = koraNaiveThemeOverrides()

// ADR-010 D3/D7: the one place the active language is applied, and the Naive UI locale it implies.
useLocaleSync()
const { effectiveLocale } = useEffectiveLocale()
const naiveLocale = computed(() => NAIVE_LOCALES[effectiveLocale.value])

// KORA-125: a token Kora refuses as a closed account is final; show the closed-account page.
const router = useRouter()
const authStore = useAuthStore()
watch(
  () => authStore.accountClosed,
  closed => {
    if (closed) void router.replace('/account/closed')
  },
  { immediate: true }
)
// KORA-148: Kora creates users only for invited or imported members; anyone else sees why.
watch(
  () => authStore.signupNotInvited,
  notInvited => {
    if (notInvited) void router.replace('/account/not-invited')
  },
  { immediate: true }
)
// KORA-152: an invited member's account is linked only once the sign-in's email is verified.
watch(
  () => authStore.emailNotVerified,
  notVerified => {
    if (notVerified) void router.replace('/account/verify-email')
  },
  { immediate: true }
)
</script>

<template>
  <NConfigProvider
    :theme-overrides="themeOverrides"
    :locale="naiveLocale.locale"
    :date-locale="naiveLocale.dateLocale"
  >
    <NGlobalStyle />
    <NMessageProvider>
      <NDialogProvider>
        <RouterView v-slot="{ Component, route }">
          <component
            :is="Component"
            :key="route.name === 'consent-confirm' ? route.path : undefined"
          />
        </RouterView>
      </NDialogProvider>
    </NMessageProvider>
  </NConfigProvider>
</template>

<style>
#app {
  min-height: 100vh;
  width: 100%;
}
</style>
